/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global Office */

import { createClient, RoomType } from "matrix-js-sdk";
import { addJitsiWidgetLite } from "../../src/taskpane/utils/addJitsiWidgetLite";

function getRoomPermalink(roomId: string, isExternal: boolean, creds: any): string {
  const permalinkPrefix =
    creds.wellKnown?.["config.client.salax"]?.permalink_prefix || creds.wellKnown?.permalink_prefix;
  let base;
  if (permalinkPrefix) {
    base = `${permalinkPrefix.replace(/\/+$/, "")}/#/${roomId}`;
  } else if (creds.webClientUrl) {
    base = `${creds.webClientUrl.replace(/\/+$/, "")}/#/${roomId}`;
  } else {
    base = `https://salax.ssh.net/#/${roomId}`;
  }
  if (isExternal) {
    const afterHash = base.split("#/")[1] || "";
    if (afterHash.includes("?")) {
      base += "&external=true";
    } else {
      base += "?external=true";
    }
  }
  return base;
}

async function createInternalMeeting(event: Office.AddinCommands.Event) {
  // 1. Try to get credentials
  const credsStr = Office.context.roamingSettings.get("salaxCreds");
  let creds = null;
  if (credsStr) {
    creds = JSON.parse(credsStr);
  }

  console.log("Creating internal meeting with creds:", creds);

  if (!creds || !creds.accessToken) {
    Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXCredsMissing", {
      type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
      message: "Please click the 'Configure a meeting' button to sign in first",
    });
    event.completed();
    return;
  }

  try {
    const client = createClient({
      baseUrl: creds.homeserverUrl,
      accessToken: creds.accessToken,
      userId: creds.userId,
      deviceId: creds.deviceId,
    });

    await client.startClient({ initialSyncLimit: 1 });

    // Step 1: Create room with placeholder name
    const tempRoomName = "SalaX Outlook Meeting";
    const roomOptions = {
      name: tempRoomName,
      preset: "public_chat",
      visibility: "public",
      creation_content: { type: RoomType.ElementVideo },
      power_level_content_override: {
        users: { [creds.userId]: 200 },
      },
    };

    const { room_id } = await client.createRoom(roomOptions);

    // Wait for room to exist
    await new Promise((resolve) => {
      const fromCache = client.getRoom(room_id);
      if (fromCache) return resolve(fromCache);
      const onRoom = (r) => {
        if (r.roomId === room_id) {
          client.removeListener("Room", onRoom);
          resolve(r);
        }
      };
      client.on("Room", onRoom);
    });

    // Step 2: Compose final name (e.g., "ABCD_July-7")
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0"); // JS months are 0-based
    const year = String(now.getFullYear()).slice(-2); // Last two digits
    const shortId = room_id.startsWith("!") ? room_id.slice(1, 5) : room_id.slice(0, 4);

    const finalRoomName = `${shortId} - ${day}.${month}.${year}`;

    // Step 3: Set the real room name
    await client.setRoomName(room_id, finalRoomName);

    // Step 4: Use finalRoomName everywhere else
    await addJitsiWidgetLite({
      client,
      roomId: room_id,
      roomName: finalRoomName,
      jitsiDomain: "call.ssh.sshcollabx.net",
      useJwt: true,
      isVideoChannel: true,
      ifExternalMeeting: false,
    });

    await client.setPowerLevel(room_id, creds.userId, 100);

    const publicLink = getRoomPermalink(room_id, false, creds);
    const title = `SalaX Meeting – ${finalRoomName}`;
    // --- Set the subject (event title) ---
    Office.context.mailbox.item.subject.setAsync(title, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to set subject:", result.error);
      }
    });

    // --- Set the location field to the join link ---
    Office.context.mailbox.item.location.setAsync(publicLink, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to set location:", result.error);
      }
    });

    // --- Compose the meeting body ---
    const meetingHtml = `
      <br><br><br><br><br>
      <hr style="border: none; border-top: 2px solid #eaeaea; margin: 24px 0;">
      <div style="font-family: 'Segoe UI', Arial, sans-serif;">
        <div style="font-size: 26px; font-weight: 700; margin-bottom: 12px;">SalaX Secure Messaging</div>
        <a href="${publicLink}"
           target="_blank"
           style="
              color: #0078d4;
              text-decoration: underline;
              font-size: 23px;
              font-weight: bold;
              letter-spacing: 0.2px;
              line-height: 2.2;
              display: inline-block;
              margin-bottom: 14px;
           "
        >Join the meeting now</a>
        <br />
               <span>Video Room Name: ${finalRoomName}</span>

      </div>
    `;

    Office.context.mailbox.item?.body.setAsync(
      meetingHtml,
      { coercionType: Office.CoercionType.Html },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXMeetingSuccess", {
            type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
            message: "SalaX meeting link added.",
            icon: "Icon.80x80",
            persistent: false,
          });
        } else {
          Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXMeetingError", {
            type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
            message: "Failed to insert SalaX meeting link.",
          });
        }
        try {
          client.stopClient();
        } catch {}
        event.completed();
      }
    );
  } catch (e) {
    Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXMeetingError", {
      type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
      message: "SalaX meeting failed: " + e?.toString(),
    });
    event.completed();
  }
}

async function createExternalMeeting(event: Office.AddinCommands.Event) {
  // 1. Try to get credentials
  const credsStr = Office.context.roamingSettings.get("salaxCreds");
  let creds = null;
  if (credsStr) {
    creds = JSON.parse(credsStr);
  }

  console.log("Creating external meeting with creds:", creds);

  if (!creds || !creds.accessToken) {
    Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXCredsMissing", {
      type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
      message: "Please click the SalaX 'Settings' button to sign in before creating a meeting.",
    });
    event.completed();
    return;
  }

  try {
    const client = createClient({
      baseUrl: creds.homeserverUrl,
      accessToken: creds.accessToken,
      userId: creds.userId,
      deviceId: creds.deviceId,
    });

    await client.startClient({ initialSyncLimit: 1 });

    const roomName = "SalaX External Meeting " + new Date().toLocaleString();
    const roomOptions = {
      name: roomName,
      preset: "private_chat",
      visibility: "private",
      creation_content: { type: RoomType.ExternalVideoCall },
      power_level_content_override: {
        users: { [creds.userId]: 200 },
      },
    };

    const { room_id } = await client.createRoom(roomOptions);

    await new Promise((resolve) => {
      const fromCache = client.getRoom(room_id);
      if (fromCache) return resolve(fromCache);
      const onRoom = (r) => {
        if (r.roomId === room_id) {
          client.removeListener("Room", onRoom);
          resolve(r);
        }
      };
      client.on("Room", onRoom);
    });

    await addJitsiWidgetLite({
      client,
      roomId: room_id,
      roomName: roomName,
      jitsiDomain: "call.ssh.sshcollabx.net",
      useJwt: true,
      isVideoChannel: true,
      ifExternalMeeting: true,
    });

    await client.setPowerLevel(room_id, creds.userId, 100);

    // Create both links (external/public and organizer/internal)
    const publicLink = getRoomPermalink(room_id, true, creds); // ?external=true
    const organizerLink = getRoomPermalink(room_id, false, creds); // no external param

    // --- Set the subject (event title) ---
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0"); // JS months are 0-based
    const year = String(now.getFullYear()).slice(-2); // Last two digits
    const shortId = room_id.startsWith("!") ? room_id.slice(1, 5) : room_id.slice(0, 4);

    const finalRoomName = `${shortId} - ${day}.${month}.${year}`;
    const meetingTitle = `SalaX Meeting – ${finalRoomName}`;
    Office.context.mailbox.item.subject.setAsync(meetingTitle, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to set subject:", result.error);
      }
    });

    // --- Set the location field to the public join link ---
    Office.context.mailbox.item.location.setAsync(publicLink, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to set location:", result.error);
      }
    });

    // --- Build Outlook body HTML like the UI ---
    const meetingHtml = `
      <br><br><br><br><br>
      <hr style="border: none; border-top: 2px solid #eaeaea; margin: 24px 0;">
      <div style="font-family: 'Segoe UI', Arial, sans-serif;">
        <div style="font-size: 26px; font-weight: 700; margin-bottom: 12px;">SalaX Secure Messaging</div>
        <a href="${publicLink}"
           target="_blank"
           style="
              color: #0078d4;
              text-decoration: underline;
              font-size: 23px;
              font-weight: bold;
              letter-spacing: 0.2px;
              line-height: 2.2;
              display: inline-block;
              margin-bottom: 7px;
           "
        >Join the meeting now</a>
    
        <div style=" font-size:15px;">
          for meeting organizers: 
          <a href="${organizerLink}" target="_blank" style="color:#0078d4; text-decoration:underline;">start the meeting</a>
        </div>

           <br />
           <span>Video Room Name: ${finalRoomName}</span>

      </div>
    `;

    Office.context.mailbox.item?.body.setAsync(
      meetingHtml,
      { coercionType: Office.CoercionType.Html },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXMeetingSuccess", {
            type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
            message: "SalaX external meeting link added.",
            icon: "Icon.80x80",
            persistent: false,
          });
        } else {
          Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXMeetingError", {
            type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
            message: "Failed to insert SalaX external meeting link.",
          });
        }
        try {
          client.stopClient();
        } catch {}
        event.completed();
      }
    );
  } catch (e) {
    Office.context.mailbox.item?.notificationMessages.replaceAsync("SalaXMeetingError", {
      type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
      message: "SalaX external meeting failed: " + e?.toString(),
    });
    event.completed();
  }
}

Office.actions.associate("createInternalMeeting", createInternalMeeting);

Office.actions.associate("createExternalMeeting", createExternalMeeting);

Office.onReady(() => {
  // If needed, Office.js is ready to be called.
});

/**
 * Shows a notification when the add-in command is executed.
 * @param event
 */
function action(event: Office.AddinCommands.Event) {
  const message: Office.NotificationMessageDetails = {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: "Performed action.",
    icon: "Icon.80x80",
    persistent: true,
  };

  // Show a notification message.
  Office.context.mailbox.item?.notificationMessages.replaceAsync(
    "ActionPerformanceNotification",
    message
  );

  // Be sure to indicate when the add-in command function is complete.
  event.completed();
}

// Register the function with Office.
Office.actions.associate("action", action);
